import { Crosshair } from "lucide-react";
import { useI18n } from "../i18n";
import { Button } from "./ui/button";
import type { UseSpellAoeFitReturn } from "../hooks/useSpellAoeFit";

type FitShape = "cone" | "sphere" | "cube" | "line" | "cylinder";

/** The card of the fit mode: what is fitted, a hint, «Оставить на карте» / «Отмена». Top-left of the map, under the snap row: the bottom is the chat and the toasts, the middle is where the HUD floats. */
export function SpellAoeFitPanel({ fit }: { fit: UseSpellAoeFitReturn }) {
  const { t } = useI18n();
  if (!fit.active) return null;
  const shape = fit.fit?.shape;
  const size = fit.fit?.sizeFt;
  const title = shape && size !== undefined
    ? t("spellAoe.title")
        .replace("{spell}", fit.spellName)
        .replace("{shape}", t(`aoe.${shape as FitShape}`))
        .replace("{size}", String(size))
    : fit.spellName;
  const pointPlaced = shape === "sphere" || shape === "cylinder";
  const message = fit.fit?.outOfRange
    ? t("spellAoe.outOfRange").replace("{range}", String(fit.rangeFt ?? ""))
    : fit.mode === "point-turn" ? t("spellAoe.hintOriginTurn") : pointPlaced ? t("spellAoe.hintAim") : t("spellAoe.hintCone");
  return (
    <div
      data-testid="spell-aoe-fit-panel"
      data-fit={fit.fit ? `${fit.fit.shape} ${Math.round(fit.fit.originX)},${Math.round(fit.fit.originY)} ${Math.round(fit.fit.rotation)}` : undefined}
      className="absolute left-2 top-2 sm:left-18 sm:top-32 w-64 flex flex-col gap-2 rounded border border-line bg-panel p-3 shadow-lg"
      style={{ zIndex: "var(--z-panel)" }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-2">
        <Crosshair className="mt-0.5 size-3.5 shrink-0 text-hl" aria-hidden />
        <div className="flex min-w-0 flex-col">
          <span className="text-ui font-semibold text-fg" data-testid="spell-aoe-fit-title">{title}</span>
          <span
            className={fit.fit?.outOfRange ? "text-meta text-danger" : "text-meta text-muted-foreground"}
            data-testid="spell-aoe-fit-hint"
          >
            {message}
          </span>
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={!fit.canKeep} onClick={fit.keep} data-testid="spell-aoe-fit-keep">
          {t("spellAoe.keep")}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={fit.cancel} data-testid="spell-aoe-fit-cancel">
          {t("spellAoe.cancel")}
        </Button>
      </div>
    </div>
  );
}
